import * as Sentry from '@sentry/vue'
import { ROOT_API } from './config'

const FILTERED = '[Filtered]'
const EMAIL = /[^\s@"'<>()]+@[^\s@"'<>()]+\.[a-z]{2,}/gi

const scrubText = text =>
  typeof text === 'string' ? text.replace(EMAIL, FILTERED) : text

// The password reset and e-mail verification links carry their token in the query string,
// the sign-up page asks GET /company/isvalid/:token with the company invitation token
const scrubUrl = url =>
  typeof url === 'string'
    ? url.replace(/[?#].*$/, '').replace(/(\/isvalid\/)[^/]+/, `$1${FILTERED}`)
    : url

// Console lines are dropped, the others lose query strings and e-mail addresses
const scrubBreadcrumb = breadcrumb => {
  if (breadcrumb.category === 'console') {
    return null
  }
  const { data } = breadcrumb
  if (data) {
    for (const key of ['url', 'from', 'to']) {
      if (data[key]) {
        data[key] = scrubUrl(data[key])
      }
    }
  }
  breadcrumb.message = scrubText(breadcrumb.message)
  return breadcrumb
}

// A failed API call is not sent: a 4xx is the API saying no (a wrong password, an expired
// session) and is shown to the user, a 5xx is reported by the API itself, and a request
// without an answer is the user's connection
const isApiError = error => Boolean(error && error.isAxiosError)

const scrubEvent = (event, hint) => {
  if (isApiError(hint && hint.originalException)) {
    return null
  }
  if (event.request) {
    event.request.url = scrubUrl(event.request.url)
    const { headers } = event.request
    if (headers && headers.Referer) {
      headers.Referer = scrubUrl(headers.Referer)
    }
    delete event.request.query_string
    delete event.request.cookies
  }
  // Without an address of its own the receiver stores the sender's IP and city; 0.0.0.0 is
  // kept as is (null is not)
  event.user = { ip_address: '0.0.0.0' }
  event.message = scrubText(event.message)
  const values = (event.exception && event.exception.values) || []
  for (const exception of values) {
    exception.value = scrubText(exception.value)
  }
  return event
}

// Only builds given VUE_APP_SENTRY_DSN report errors. Tracing stays off
export const initSentry = Vue => {
  const dsn = process.env.VUE_APP_SENTRY_DSN
  if (!dsn) {
    return
  }
  Sentry.init({
    Vue,
    dsn,
    environment: process.env.NODE_ENV,
    sendDefaultPii: false,
    // props hold users, companies and answers
    attachProps: false,
    // Vue's own console message stays
    logErrors: true,
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
    // through our API: ad blockers stop requests to the error tracker's own address
    tunnel: `${ROOT_API}app-events`,
    // only errors: no session on every page view, no report of dropped events on page hide,
    // each of which would be one more request to the API
    autoSessionTracking: false,
    sendClientReports: false,
    // reCAPTCHA v3 rejects its promise with a bare 'Timeout' string on slow connections
    ignoreErrors: [
      /^Non-Error promise rejection captured with value: Timeout$/
    ],
    // errors thrown inside Google's scripts (reCAPTCHA, gtag) and browser extensions
    denyUrls: [
      /recaptcha\.net/,
      /google\.com\/recaptcha/,
      /gstatic\.com/,
      /googletagmanager\.com/,
      /google-analytics\.com/,
      /^(chrome|moz|safari(-web)?)-extension:\/\//
    ]
  })
}
